import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import CardTable, { CardTableProps } from '@casops/components/tables/card-table/CardTable';
import CollectionTableHeader, {
  CollectionTableHeaderProps,
} from '../collection-table-header/CollectionTableHeader';
import { GridProps } from '@dmc/ui/tables';
import { GridControlled } from '@casops/components/tables/grid/GridControlled';

export interface CollectionTableProps<T extends object>
  extends CardTableProps,
    CollectionTableHeaderProps,
    Pick<
      GridProps<T>,
      | 'sliceKey'
      | 'rowData'
      | 'defaultColDef'
      | 'columnDefs'
      | 'onRowClick'
      | 'rowSelectMode'
      | 'isRowSelectable'
      | 'getRowId'
      | 'rowClassRules'
      | 'ensureNodeVisibleCallback'
      | 'ensureNodeVisibleKey'
      | 'includeHiddenColumnsInQuickFilter'
    > {}

export const CollectionTable = <T extends object>({
  modelName,
  sliceKey,
  rowData,
  defaultColDef,
  columnDefs,
  hasData,
  noDataDescription,
  onRowClick,
  rowClassRules,
  rowSelectMode,
  isRowSelectable,
  getRowId,
  filterProps,
  secondaryModelName,
  countAll,
  showSummaryInTable = false,
  enableColumnSelection = false,
  ensureNodeVisibleCallback,
  ensureNodeVisibleKey,
  includeHiddenColumnsInQuickFilter,
}: CollectionTableProps<T>): ReactElement => {
  const { t } = useTranslation();

  const showHeader = enableColumnSelection || showSummaryInTable;

  return (
    <CardTable
      hasData={hasData}
      noDataDescription={noDataDescription}
      header={
        showHeader ? (
          <CollectionTableHeader
            modelName={modelName}
            secondaryModelName={secondaryModelName}
            filterProps={filterProps}
            countAll={countAll}
            showSummaryInTable={showSummaryInTable}
            enableColumnSelection={enableColumnSelection}
          />
        ) : undefined
      }
    >
      <GridControlled<T>
        sliceKey={sliceKey}
        rowData={rowData}
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        noRowsMessage={t('Model.Collection.NoItems', { modelName })}
        onRowClick={onRowClick}
        rowClassRules={rowClassRules}
        suppressHorizontalScroll={false}
        rowSelectMode={rowSelectMode}
        isRowSelectable={isRowSelectable}
        getRowId={getRowId}
        ensureNodeVisibleCallback={ensureNodeVisibleCallback}
        ensureNodeVisibleKey={ensureNodeVisibleKey}
        includeHiddenColumnsInQuickFilter={includeHiddenColumnsInQuickFilter}
      />
    </CardTable>
  );
};

export default CollectionTable;
